import { useApp } from '@/contexts/AppContext';
import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { Logo } from '@/components/Logo';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, Plus, Zap } from 'lucide-react';

export default function Dashboard() {
  const { selectedPropertyId, selectedPlantId, properties, loading, isInstaller, setPropertyScope } = useApp();
  const navigate = useNavigate();

  // Installers with no properties → redirect to onboarding welcome
  useEffect(() => {
    if (!loading && properties.length === 0 && isInstaller) {
      navigate('/app/onboarding/welcome');
    }
  }, [loading, properties, isInstaller, navigate]);

  // Installer → fleet dashboard; Homeowner → property dashboard
  useEffect(() => {
    if (loading) return;
    if (isInstaller) {
      navigate('/app/fleet', { replace: true });
    } else if (selectedPropertyId) {
      const property = properties.find(p => p.id === selectedPropertyId);
      if (property) setPropertyScope(selectedPropertyId, property.name);
      navigate(`/app/property/${selectedPropertyId}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once when loading completes
  }, [loading, isInstaller, selectedPropertyId]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-2">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
        <span className="text-sm text-wt-text-secondary">Loading…</span>
      </div>
    );
  }

  // No properties - show empty state (different for installer vs homeowner)
  if (properties.length === 0) {
    if (!isInstaller) {
      // Homeowner with no assigned properties
      return (
        <div className="flex items-center justify-center min-h-[400px]">
          <Card className="max-w-md text-center">
            <CardHeader>
              <div className="mx-auto mb-4 flex items-center justify-center">
                <Logo variant="fullTransparent" size="h-16" />
              </div>
              <CardTitle>No Properties Assigned</CardTitle>
              <CardDescription>
                Your installer hasn't assigned any properties to your account yet. Please contact them to get access.
              </CardDescription>
            </CardHeader>
          </Card>
        </div>
      );
    }
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="max-w-md text-center">
          <CardHeader>
            <div className="mx-auto mb-4 flex items-center justify-center">
              <Logo variant="fullTransparent" size="h-16" />
            </div>
            <CardTitle>Welcome to Watt Tower</CardTitle>
            <CardDescription>
              Get started by setting up your first property and connecting your solar equipment.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/app/onboarding/welcome')} className="w-full">
              <Plus className="w-4 h-4 mr-2" />
              Start Setup
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-[400px]">
      <Card className="max-w-md text-center">
        <CardHeader>
          <CardTitle>Select a Property</CardTitle>
          <CardDescription>
            Choose a property from the dropdown above to view its command center.
          </CardDescription>
        </CardHeader>
      </Card>
    </div>
  );
}
